import { getWalletAddress, getWalletProviderId, setWalletAddress } from '@/utils/auth-token';
import { getUsdcContractAddresses } from '@/utils/trade-contracts';
import { readWalletTokenBalance } from '@/utils/wallet-token-balance';
import { normalizeWalletProviderId } from '@/utils/wallet-provider';
import { resolveFastWalletTargetsForBalance } from '@/utils/wallet-address-sources';

/** 链上余额查询对应的币种（与 readUsdcBalanceForAddress 一致） */
export const WALLET_BALANCE_CURRENCY = 'USDC';

export function getWalletBalanceCurrency() {
  return WALLET_BALANCE_CURRENCY;
}

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

const BALANCE_CACHE_MS = 30_000;
const ZERO_BALANCE_CACHE_MS = 8_000;
const balanceCache = new Map();
const inflightReads = new Map();
let inflightSummary = null;

async function readUsdcBalanceForAddressInner(ownerAddress) {
  const balances = await Promise.all(
    getUsdcContractAddresses().map((contract) =>
      readWalletTokenBalance(contract, ownerAddress, 137)
    )
  );
  return balances.reduce((sum, amount) => sum + amount, 0);
}

/**
 * 通过服务端读取指定地址的 USDC 余额（短缓存合并页面请求）
 */
export async function readUsdcBalanceForAddress(ownerAddress) {
  const owner = normalizeAddress(ownerAddress);
  if (!owner) return 0;

  const cacheKey = `${getWalletProviderId()}:${owner.toLowerCase()}`;
  const cached = balanceCache.get(cacheKey);
  if (cached && cached.expires > Date.now()) {
    return cached.value;
  }

  if (inflightReads.has(cacheKey)) {
    return inflightReads.get(cacheKey);
  }

  const task = readUsdcBalanceForAddressInner(owner)
    .then((value) => {
      const safe = Number.isFinite(value) ? value : 0;
      balanceCache.set(cacheKey, {
        value: safe,
        expires: Date.now() + (safe > 0 ? BALANCE_CACHE_MS : ZERO_BALANCE_CACHE_MS),
      });
      return safe;
    })
    .finally(() => {
      inflightReads.delete(cacheKey);
    });

  inflightReads.set(cacheKey, task);
  return task;
}

/** 清除余额缓存（充值/提现成功后调用） */
export function invalidateUsdcBalanceCache() {
  balanceCache.clear();
}

/** 当前登录方式对应的钱包地址列表（通常仅 1 个） */
export async function resolveUserWalletTargets() {
  return resolveFastWalletTargetsForBalance();
}

async function readWalletTargetBalance(target) {
  try {
    const balance = await readUsdcBalanceForAddress(target.address);
    return Number.isFinite(balance) ? balance : 0;
  } catch (err) {
    console.warn('[wallet-balance] read USDC failed', target.address, err);
    return 0;
  }
}

async function fetchUserWalletBalanceSummaryInner() {
  const loginProvider = normalizeWalletProviderId(getWalletProviderId());
  const targets = await resolveFastWalletTargetsForBalance();
  if (!targets.length) {
    return { wallets: [], total: 0, active: null, currency: WALLET_BALANCE_CURRENCY };
  }

  const wallets = await Promise.all(
    targets.map(async (target) => ({
      ...target,
      balance: await readWalletTargetBalance(target),
    }))
  );

  const displayWallets = wallets;
  const primaryTarget = targets[0] ?? null;

  // 与交易/充值一致：Privy 用 signer 顺序首个地址，不按「有余额的其他地址」抬高展示
  const active =
    displayWallets.find((row) => addressesEqual(row.address, primaryTarget?.address)) ||
    displayWallets.find((row) => addressesEqual(row.address, getWalletAddress())) ||
    displayWallets.find((row) => row.providerId === loginProvider) ||
    displayWallets[0] ||
    null;

  if (active?.address && getWalletProviderId() === 'privy') {
    setWalletAddress(active.address);
  }

  const total = Number(active?.balance ?? 0);

  return {
    wallets: displayWallets,
    total: Number.isFinite(total) ? total : 0,
    active,
    currency: WALLET_BALANCE_CURRENCY,
  };
}

/**
 * 读取当前登录钱包的链上 USDC（Google/邮箱 → Privy；钱包登录 → 对应插件钱包）
 */
export async function fetchUserWalletBalanceSummary() {
  if (inflightSummary) return inflightSummary;
  inflightSummary = fetchUserWalletBalanceSummaryInner().finally(() => {
    inflightSummary = null;
  });
  return inflightSummary;
}

function addressesEqual(a, b) {
  const left = normalizeAddress(a).toLowerCase();
  const right = normalizeAddress(b).toLowerCase();
  if (!left || !right) return false;
  return left === right;
}

/** 当前登录钱包链上 USDC */
export async function fetchActiveWalletUsdcBalance() {
  const summary = await fetchUserWalletBalanceSummary();
  const balance = Number(summary.active?.balance ?? summary.total ?? 0);
  return Number.isFinite(balance) ? balance : 0;
}

/** 与 fetchActiveWalletUsdcBalance 相同：仅当前登录钱包 */
export async function fetchAllWalletsUsdcBalance() {
  return fetchActiveWalletUsdcBalance();
}
